/**
 * TanStack Query hooks for `conversion-crud-view-edit-reject-permission-scope`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	check,
	create,
	list,
	conversionCrudViewEditRejectPermissionScopeQueryKeys,
	type CheckParams,
	type CheckResponse,
	type CreateConversionCrudViewEditRejectPermissionScopeBody,
	type CreateConversionCrudViewEditRejectPermissionScopeResponse,
	type ListConversionCrudViewEditRejectPermissionScopeResponse,
	type ListFilters,
} from "./api";

export function useConversionCrudViewEditRejectPermissionScopeList(
	filters: ListFilters = {},
): UseQueryResult<ListConversionCrudViewEditRejectPermissionScopeResponse, Error> {
	return useQuery({
		queryKey: conversionCrudViewEditRejectPermissionScopeQueryKeys.list(filters),
		queryFn: () => list(filters),
	});
}

export function useConversionCrudViewEditRejectPermissionScopeCreate(): UseMutationResult<
	CreateConversionCrudViewEditRejectPermissionScopeResponse,
	Error,
	CreateConversionCrudViewEditRejectPermissionScopeBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: conversionCrudViewEditRejectPermissionScopeQueryKeys.all,
			});
		},
	});
}

export function useConversionCrudViewEditRejectPermissionScopeCheck(
	params: CheckParams | null,
): UseQueryResult<CheckResponse, Error> {
	return useQuery({
		queryKey: params
			? conversionCrudViewEditRejectPermissionScopeQueryKeys.check(params)
			: [...conversionCrudViewEditRejectPermissionScopeQueryKeys.all, "check", "disabled"],
		queryFn: () => {
			if (!params) throw new Error("check params required");
			return check(params);
		},
		enabled: Boolean(params),
	});
}
